import { h } from 'vue'
import type {
  MatchStakeholdersRequestParams,
  MatchStakeholdersRequestResponse
} from '@gat-ship/types'
import type { FormFieldMessage } from '@gat-ship/vue-components'
import StakeholderMatchingCandidates from '../components/StakeholderMatchingCandidates.vue'
import { useStakeholderApiCalls } from './useStakeholderApiCalls'

export interface StakeholderMatchMessagesOptions {
  /** Message when a candidate matches the typed value exactly. */
  exactMatchMessage: string
  /**
   * When set, non-exact matches render as a `warning` with this text and
   * an exact match upgrades to an `error` — the "similar names" UX of the
   * name field. When omitted, any candidate yields `exactMatchMessage`.
   */
  similarMessage?: (count: number) => string
  variant?: 'warning' | 'error'
}

/** Pick which field key in the params we just populated. */
function findValueKey(
  params: MatchStakeholdersRequestParams
): keyof MatchStakeholdersRequestParams {
  const keys: (keyof MatchStakeholdersRequestParams)[] = [
    'name',
    'email',
    'phone',
    'tin',
    'eori_number',
    'client_number',
    'vendor_number'
  ]
  for (const k of keys) {
    if (params[k] !== undefined) return k
  }
  return 'name'
}

/** Render the candidate list as the right message variant for the field. */
function candidateMessages(
  candidates: MatchStakeholdersRequestResponse['candidates'],
  options: StakeholderMatchMessagesOptions,
  value: string
): FormFieldMessage[] {
  const messageLong = h(StakeholderMatchingCandidates, { candidates })
  const exact = candidates.find(
    (c) => c.name.toLowerCase() === value.toLowerCase()
  )
  if (exact && options.similarMessage) {
    return [
      {
        variant: 'error',
        message: options.exactMatchMessage,
        messageLong
      }
    ]
  }
  if (options.similarMessage) {
    return [
      {
        variant: 'warning',
        message: options.similarMessage(candidates.length),
        messageLong
      }
    ]
  }
  return [
    {
      variant: options.variant ?? 'error',
      message: options.exactMatchMessage,
      messageLong
    }
  ]
}

/**
 * Duplicate-stakeholder detection for `messages-async` form fields.
 *
 * `buildMatchMessages` returns an async handler that queries
 * `matchStakeholdersRequest` for candidates matching the typed value and
 * renders them as a `FormFieldMessage[]` with a rich
 * `StakeholderMatchingCandidates` list in the `messageLong` slot. Unlike
 * `validators.onChangeAsync`, `messages-async` keeps the result separate
 * from the submit gate (warnings don't block save) — the right semantic
 * for a "possible duplicate" hint.
 *
 * Used by the create-stakeholder form and the three stakeholder edit
 * modals; the edit flow passes `excludeStakeholder` so the record being
 * edited never reports itself as a duplicate.
 */
export function useStakeholderMatchMessages(
  context: {
    /**
     * Edit flow: the stakeholder being edited, whose own record is
     * filtered out of the candidates. Returning `undefined` (modal not
     * seeded yet) skips matching entirely.
     */
    excludeStakeholder?: () => { id: string } | undefined
  } = {}
) {
  const { matchStakeholdersRequest } = useStakeholderApiCalls()

  function buildMatchMessages(
    params:
      | MatchStakeholdersRequestParams
      | (() => MatchStakeholdersRequestParams),
    options: StakeholderMatchMessagesOptions
  ) {
    return async ({
      value,
      signal
    }: {
      value: string
      signal: AbortSignal
    }): Promise<FormFieldMessage[] | undefined> => {
      if (!value || value.length < 1) return
      const excluded = context.excludeStakeholder?.()
      if (context.excludeStakeholder && !excluded) return
      const resolved = typeof params === 'function' ? params() : params
      const response = await matchStakeholdersRequest(
        {
          ...resolved,
          [findValueKey(resolved)]: value.trim()
        } as MatchStakeholdersRequestParams,
        { signal }
      )
      const candidates = excluded
        ? response.candidates.filter((c) => c.id !== excluded.id)
        : response.candidates
      if (candidates.length === 0) return
      return candidateMessages(candidates, options, value)
    }
  }

  return { buildMatchMessages }
}
